import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRightLeft, Building2, CalendarClock, CalendarDays, CalendarPlus, Check, CheckSquare, Clock,
  Copy, ExternalLink, History, IdCard, Mail, MoreHorizontal, Pencil, Phone, PhoneCall, PhoneIncoming,
  PhoneMissed, PhoneOutgoing, Plus, StickyNote, Target, Trash2, TrendingUp, Unlink, X,
} from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { toast } from "sonner";
import { Loader } from "@/crm/ui";
import Modal from "@/crm/components/Modal";
import AddressAutocomplete from "@/crm/components/AddressAutocomplete";
import { useTwilio } from "@/context/TwilioVoice";
import { RESULT_META } from "@/crm/components/telephony/shared";
import { cn } from "@/lib/utils";

const FIELDS = [
  ["prenom", "Prénom"], ["nom", "Nom"], ["fonction", "Fonction"],
  ["telephone", "Téléphone"], ["email", "Email"],
];

// Gabarits de la fiche (palette neutre « console pro » du handoff).
const GAP = "gap-[clamp(12px,1.6vw,18px)]";
const CARD = "rounded-2xl border border-[#e3e3e3] bg-white p-[clamp(16px,2vw,22px)]";
const H2 = "m-0 flex items-center gap-2 font-heading text-base font-bold text-[#0f172a]";
const BTN = "inline-flex items-center justify-center gap-2 rounded-xl border border-[#e3e3e3] bg-white text-sm font-semibold text-[#334155] transition-all duration-200 ease-out hover:border-[#d0d0d0] hover:bg-[#f1f1f1] active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white";
const BTN_CALL = "inline-flex items-center justify-center gap-2 rounded-xl bg-[#008060] text-sm font-semibold text-white shadow-[0_6px_14px_-8px_rgba(0,128,96,.9)] transition-all duration-200 ease-out hover:bg-[#004c3f] active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-40";
const BTN_BLUE = "inline-flex items-center justify-center gap-[7px] rounded-[11px] bg-[#005bd3] text-[13px] font-semibold text-white transition-all duration-200 ease-out hover:bg-[#00479f] active:scale-[.98] disabled:opacity-50";
const BTN_SMALL = "inline-flex items-center justify-center gap-[7px] rounded-[11px] border border-[#d0d0d0] bg-white text-[13px] font-semibold text-[#303030] transition-all duration-200 ease-out hover:bg-[#fafafa] active:scale-[.98] disabled:opacity-50";
const INPUT = "h-[42px] w-full rounded-[10px] border border-[#d0d0d0] bg-white px-3 text-sm text-[#0f172a] outline-none transition-all focus:border-[#005bd3] focus:ring-[3px] focus:ring-[rgba(0,91,211,.16)]";
const LABEL = "mb-1.5 block text-xs font-semibold text-[#64748b]";

// ---------- Dates ----------
const DAY = 86400000;
const relTime = (iso) => {
  if (!iso) return null;
  const d = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(d)) return null;
  const m = Math.floor(d / 60000);
  if (m < 1) return "À l'instant";
  if (m < 60) return `Il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Il y a ${h} h`;
  const j = Math.floor(h / 24);
  if (j < 30) return `Il y a ${j} j`;
  const mo = Math.floor(j / 30);
  if (mo < 12) return `Il y a ${mo} mois`;
  const an = Math.floor(mo / 12);
  return `Il y a ${an} an${an > 1 ? "s" : ""}`;
};
// Jours calendaires entre aujourd'hui et une date (ISO ou AAAA-MM-JJ), sans
// dépendre du fuseau : une échéance « 15/09 » reste le 15/09.
const daysUntil = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  const now = new Date();
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) / DAY);
};
const dueLabel = (iso) => {
  const n = daysUntil(iso);
  if (n === null) return { label: "Sans échéance", late: false };
  if (n < 0) return { label: `En retard de ${-n} j`, late: true };
  if (n === 0) return { label: "Aujourd'hui", late: false };
  if (n === 1) return { label: "Demain", late: false };
  return { label: `Dans ${n} j`, late: false };
};
const fmtShort = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso)
  .toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) : "—");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR",
  { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const fmtDur = (s) => `${Math.floor(s / 60)}m${String(s % 60).padStart(2, "0")}`;
const initials = (...parts) => parts.filter(Boolean).map((p) => p.trim()[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";

// ---------- Historique unifié ----------
const CALL_TAG = {
  REPONDU: ["#e7f5f1", "#005c48"], TERMINE: ["#e7f5f1", "#005c48"], EN_COURS: ["#eaf4ff", "#00527c"],
  MESSAGERIE: ["#fdf3e7", "#8a6116"], MANQUE: ["#fdf3e7", "#8a6116"], ABANDONNE: ["#fdf3e7", "#8a6116"],
  REJETE: ["#fdeceb", "#b42318"], RENVOI_SPECIAL: ["#fdeceb", "#b42318"],
};
const ACTIVITY_META = {
  CALL: { label: "Appel journalisé", icon: PhoneCall, bg: "#f2f4f7", fg: "#4a4a4a" },
  EMAIL: { label: "Email envoyé", icon: Mail, bg: "#eaf4ff", fg: "#00527c" },
  MEETING: { label: "Rendez-vous", icon: CalendarDays, bg: "#f3e8ff", fg: "#6d28d9" },
  NOTE: { label: "Note", icon: StickyNote, bg: "#fdf3e7", fg: "#8a6116" },
  STATUS_CHANGE: { label: "Changement de statut", icon: ArrowRightLeft, bg: "#f2f4f7", fg: "#4a4a4a" },
  TASK: { label: "Tâche terminée", icon: CheckSquare, bg: "#e7f5f1", fg: "#008060" },
};

function buildTimeline(history) {
  const calls = (history.calls || []).map((c) => {
    const missed = ["MANQUE", "ABANDONNE"].includes(c.result);
    const [tagBg, tagFg] = CALL_TAG[c.result] || ["#f2f4f7", "#4a4a4a"];
    return {
      id: `call-${c.id}`, kind: "calls", at: c.created_at,
      icon: missed ? PhoneMissed : c.direction === "ENTRANT" ? PhoneIncoming : PhoneOutgoing,
      bg: missed ? "#fdf3e7" : "#f2f4f7", fg: missed ? "#8a6116" : "#4a4a4a",
      title: c.direction === "ENTRANT" ? "Appel entrant" : "Appel sortant",
      tag: RESULT_META[c.result]?.label || c.result, tagBg, tagFg,
      detail: [c.outcome, c.note].filter(Boolean).join(" — ") || c.phone_number || "",
      meta: [fmtDateTime(c.created_at), c.call_duration > 0 ? fmtDur(c.call_duration) : null,
        c.answered_by_name || c.created_by_name].filter(Boolean).join(" · "),
    };
  });
  const acts = (history.activities || []).map((a) => {
    const m = ACTIVITY_META[a.type] || { label: a.type || "Activité", icon: CheckSquare, bg: "#e7f5f1", fg: "#008060" };
    return {
      id: `act-${a.id}`, kind: "activities", at: a.created_at,
      icon: m.icon, bg: m.bg, fg: m.fg,
      title: a.subject || m.label,
      tag: a.result || m.label, tagBg: "#f2f4f7", tagFg: "#4a4a4a",
      detail: a.description || "",
      meta: [fmtDateTime(a.created_at), a.user_name].filter(Boolean).join(" · "),
    };
  });
  return [...calls, ...acts].sort((x, y) => String(y.at || "").localeCompare(String(x.at || "")));
}

const HISTORY_TABS = [["all", "Tout"], ["calls", "Appels"], ["activities", "Activités"]];
const HISTORY_PREVIEW = 6;

// ---------- Modales ----------
function ModalHeader({ title, onClose }) {
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <h3 className="m-0 font-heading text-base font-bold text-[#0f172a]">{title}</h3>
      <button type="button" onClick={onClose} aria-label="Fermer"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#94a3b8] transition-colors hover:bg-[#f2f4f7] hover:text-[#0f172a]">
        <X size={16} />
      </button>
    </div>
  );
}

const TASK_TYPES = [["MEETING", "Rendez-vous"], ["CALL", "Appel"], ["EMAIL", "Email"], ["FOLLOW_UP", "Suivi"], ["OTHER", "Autre"]];

function ContactTaskModal({ contact, fullName, defaultType = "MEETING", onClose, onSaved }) {
  const [title, setTitle] = useState(defaultType === "MEETING" ? `Rendez-vous avec ${fullName}` : "");
  const [type, setType] = useState(defaultType);
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState("medium");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!title.trim()) return toast.error("Titre requis");
    setSaving(true);
    try {
      await api.post("/tasks", { company_id: contact.company.id, title: title.trim(), type, due_date: due || null, priority });
      toast.success(type === "MEETING" ? "Rendez-vous planifié" : "Action planifiée");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const titre = defaultType === "MEETING" ? "Planifier un rendez-vous" : "Planifier une action";
  return (
    <Modal onClose={onClose} label={titre} testid="contact-task-modal">
      <ModalHeader title={titre} onClose={onClose} />
      <div className="space-y-3">
        <label className="block">
          <span className={LABEL}>Titre</span>
          <input data-testid="contact-task-title" value={title} onChange={(e) => setTitle(e.target.value)} className={INPUT} placeholder="Ex. Démo à trois" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className={LABEL}>Type</span>
            <select data-testid="contact-task-type" value={type} onChange={(e) => setType(e.target.value)} className={INPUT}>
              {TASK_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className={LABEL}>Priorité</span>
            <select data-testid="contact-task-priority" value={priority} onChange={(e) => setPriority(e.target.value)} className={INPUT}>
              {[["low", "Basse"], ["medium", "Moyenne"], ["high", "Haute"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        </div>
        <label className="block">
          <span className={LABEL}>Échéance</span>
          <input type="date" data-testid="contact-task-due" value={due} onChange={(e) => setDue(e.target.value)} className={INPUT} />
        </label>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className={cn(BTN_SMALL, "h-10 flex-1")}>Annuler</button>
          <button data-testid="contact-task-save" onClick={save} disabled={saving} className={cn(BTN_BLUE, "h-10 flex-1")}>
            {saving ? "Enregistrement…" : "Planifier"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ContactEmailModal({ contact, onClose, onSaved }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [objet, setObjet] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/email-templates").then(({ data }) => setTemplates(data || [])).catch(() => {}); }, []);

  const applyTemplate = (tid) => {
    setTemplateId(tid);
    const t = templates.find((x) => x.id === tid);
    if (t) { setObjet(t.objet || ""); setMessage(t.corps || ""); }
  };
  const send = async () => {
    if (!objet.trim()) return toast.error("Objet requis");
    setSending(true);
    try {
      const { data } = await api.post("/activities/email", {
        company_id: contact.company.id, contact_id: contact.id, destinataire: contact.email,
        objet, message, template_id: templateId || null,
      });
      if (data.resend_configured === false) toast.warning("Email SIMULÉ — configurez RESEND_API_KEY dans backend/.env pour un envoi réel");
      else toast.success("Email en cours d'envoi");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };
  return (
    <Modal onClose={onClose} label="Envoyer un email" size="lg" testid="contact-email-modal">
      <ModalHeader title="Envoyer un email" onClose={onClose} />
      <div className="space-y-3">
        <div className="flex items-center gap-2 rounded-[10px] bg-[#f2f4f7] px-3 py-2 text-[13px] text-[#4a5261]">
          <Mail size={14} className="shrink-0 text-[#8a8f98]" />
          <span className="truncate">À : <span className="font-medium text-[#0f172a]">{contact.email}</span></span>
        </div>
        {templates.length > 0 && (
          <select data-testid="contact-email-template" value={templateId} onChange={(e) => applyTemplate(e.target.value)} className={INPUT}>
            <option value="">— Sans template —</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
        <input data-testid="contact-email-objet" placeholder="Objet" value={objet} onChange={(e) => setObjet(e.target.value)} className={INPUT} />
        <textarea data-testid="contact-email-message" placeholder={"Message…  Variables : {nom} {ville} {commercial}"} value={message}
          onChange={(e) => setMessage(e.target.value)} rows={7}
          className="w-full resize-y rounded-[10px] border border-[#d0d0d0] bg-white px-3 py-2.5 text-sm leading-relaxed text-[#0f172a] outline-none transition-all focus:border-[#005bd3] focus:ring-[3px] focus:ring-[rgba(0,91,211,.16)]" />
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className={cn(BTN_SMALL, "h-10 flex-1")}>Annuler</button>
          <button data-testid="contact-email-send" onClick={send} disabled={sending} className={cn(BTN_BLUE, "h-10 flex-1")}>
            {sending ? "Envoi…" : "Envoyer"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------- Page ----------
const formFrom = (c) => ({
  prenom: c.prenom || "", nom: c.nom || "", fonction: c.fonction || "",
  telephone: c.telephone || "", email: c.email || "", notes: c.notes || "",
  adresse: c.adresse || "", code_postal: c.code_postal || "", ville: c.ville || "",
});

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tw = useTwilio();
  const [contact, setContact] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [noteEditing, setNoteEditing] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [historyTab, setHistoryTab] = useState("all");
  const [showAll, setShowAll] = useState(false);
  const [menu, setMenu] = useState(false);
  const [modal, setModal] = useState(null); // "email" | "task" | "rdv"
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [history, setHistory] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [creating, setCreating] = useState(false);
  const searchTimer = useRef(null);
  const menuRef = useRef(null);

  const adopt = useCallback((data) => { setContact(data); setForm(formFrom(data)); }, []);
  const reload = useCallback(() => api.get(`/contacts/${id}`).then(({ data }) => adopt(data)), [id, adopt]);

  const loadHistory = useCallback(() => {
    api.get(`/contacts/${id}/history`).then(({ data }) => setHistory(data))
      .catch(() => setHistory({ calls: [], activities: [] }));
  }, [id]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  useEffect(() => {
    reload().catch((e) => { toast.error(apiError(e)); navigate("/contacts"); });
  }, [reload, navigate]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!search.trim()) { setResults([]); return; }
    searchTimer.current = setTimeout(() => {
      api.get(`/companies?search=${encodeURIComponent(search.trim())}&limit=6`)
        .then(({ data }) => setResults(data.items || data.companies || []))
        .catch(() => setResults([]));
    }, 300);
  }, [search]);

  // Prochaine action = la première tâche en cours de l'entreprise liée (les
  // tâches sont rattachées aux entreprises, pas aux contacts).
  const companyId = contact?.company?.id || null;
  const loadTasks = useCallback(() => {
    if (!companyId) { setTasks([]); return; }
    api.get("/tasks").then(({ data }) => setTasks((data || []).filter((t) => t.company_id === companyId)))
      .catch(() => setTasks([]));
  }, [companyId]);
  useEffect(() => { loadTasks(); }, [loadTasks]);

  // La fiche contact ne porte que l'essentiel de l'entreprise (nom, statut,
  // ville) : on complète avec la fiche entreprise pour le NAF et l'effectif.
  useEffect(() => {
    if (!companyId) { setCompanyInfo(null); return; }
    api.get(`/companies/${companyId}`).then(({ data }) => setCompanyInfo(data)).catch(() => setCompanyInfo(null));
  }, [companyId]);

  // Le menu « ⋯ » se referme d'un clic ailleurs.
  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (e) => { if (!menuRef.current?.contains(e.target)) setMenu(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menu]);

  const timeline = useMemo(() => (history ? buildTimeline(history) : []), [history]);
  const stats = useMemo(() => {
    const calls = history?.calls || [];
    const answered = calls.filter((c) => ["REPONDU", "TERMINE"].includes(c.result)).length;
    return {
      last: timeline[0]?.at || null,
      total: timeline.length,
      rate: calls.length ? `${Math.round((answered / calls.length) * 100)} %` : "—",
    };
  }, [history, timeline]);
  const nextTask = useMemo(() => tasks
    .filter((t) => t.status !== "done")
    .sort((a, b) => String(a.due_date || "9").localeCompare(String(b.due_date || "9")))[0] || null, [tasks]);

  if (!contact) return <Loader />;

  const fullName = [contact.prenom, contact.nom].filter(Boolean).join(" ") || "Contact sans nom";
  const company = contact.company;
  const softphoneVisible = !!tw && tw.capabilities?.browser_calls !== false;

  const persist = async (fields, message = "Fiche contact enregistrée") => {
    setSaving(true);
    try {
      const { data } = await api.put(`/contacts/${id}`, fields);
      adopt(data);
      if (message) toast.success(message);
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
    finally { setSaving(false); }
  };

  const saveForm = async () => {
    const { notes, ...rest } = form;
    if (await persist(rest)) setEditing(false);
  };
  const cancelEdit = () => { setForm(formFrom(contact)); setEditing(false); };
  const startNote = () => { setNoteDraft(contact.notes || ""); setNoteEditing(true); };
  const saveNote = async () => {
    if (await persist({ notes: noteDraft }, "Note enregistrée")) setNoteEditing(false);
  };

  const callContact = () => tw?.makeCall(contact.telephone,
    { contact_id: contact.id, company_id: company?.id, company_name: company?.nom });
  const copy = (value, label) => {
    if (!value) return;
    navigator.clipboard?.writeText(value).then(() => toast.success(`${label} copié`)).catch(() => {});
  };
  // Envoi tracé quand le contact est rattaché à une entreprise (le journal
  // d'emails est rattaché aux entreprises) ; sinon, le client de messagerie.
  const emailContact = () => {
    if (!contact.email) return;
    if (company) setModal("email");
    else window.location.href = `mailto:${contact.email}`;
  };

  // Création à la volée : jusqu'ici la fiche ne savait que chercher une
  // entreprise existante, sans issue quand elle n'était pas encore au CRM.
  const creerEtLier = async () => {
    const nom = search.trim();
    if (!nom) return;
    setCreating(true);
    try {
      const { data: entreprise } = await api.post("/companies", { nom, source: "fiche-contact" });
      await linkCompany(entreprise.id, entreprise.nom);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Création de l'entreprise impossible");
    } finally { setCreating(false); }
  };

  const linkCompany = async (cid, nom) => {
    try {
      await api.put(`/contacts/${id}`, { company_id: cid });
      await reload();
      setSearch(""); setResults([]);
      toast.success(`Contact lié à ${nom}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Impossible de lier cette entreprise");
    }
  };

  const unlink = async () => {
    try {
      await api.put(`/contacts/${id}`, { company_id: null });
      // On relit la fiche au lieu de se fier au corps de la réponse : c'est
      // l'état réellement enregistré qui doit s'afficher.
      await reload();
      toast.success("Contact délié — conservé comme contact libre");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Impossible de délier ce contact");
    }
  };

  const del = async () => {
    setMenu(false);
    if (!window.confirm("Supprimer ce contact ?")) return;
    try {
      await api.delete(`/contacts/${id}`);
      toast.success("Contact supprimé");
      navigate("/contacts");
    } catch (e) { toast.error(apiError(e)); }
  };

  const finishTask = async () => {
    try {
      await api.put(`/tasks/${nextTask.id}`, { status: "done" });
      toast.success("Action marquée faite");
      loadTasks();
    } catch (e) { toast.error(apiError(e)); }
  };
  // Reporter = une semaine plus tard, à partir de l'échéance (ou d'aujourd'hui
  // si elle est déjà passée).
  const postponeTask = async () => {
    const base = nextTask.due_date && daysUntil(nextTask.due_date) > 0
      ? new Date(`${nextTask.due_date.slice(0, 10)}T12:00:00`) : new Date();
    base.setDate(base.getDate() + 7);
    const due = base.toISOString().slice(0, 10);
    try {
      await api.put(`/tasks/${nextTask.id}`, { due_date: due });
      toast.success(`Action reportée au ${fmtShort(due)}`);
      loadTasks();
    } catch (e) { toast.error(apiError(e)); }
  };

  const readRows = [
    ...FIELDS.map(([k, label]) => ({ key: k, label, value: contact[k] || "", mono: k === "telephone",
      action: k === "telephone" ? "phone" : k === "email" ? "mail" : "copy" })),
    { key: "adresse", label: "Adresse", mono: false, action: "copy",
      value: [contact.adresse, [contact.code_postal, contact.ville].filter(Boolean).join(" ")].filter(Boolean).join(", ") },
  ];
  const shown = historyTab === "all" ? timeline : timeline.filter((e) => e.kind === historyTab);
  const visibleHistory = showAll ? shown : shown.slice(0, HISTORY_PREVIEW);
  const due = nextTask ? dueLabel(nextTask.due_date) : null;
  const lastLabel = stats.last ? relTime(stats.last) : "—";

  return (
    <div className={cn("mx-auto flex max-w-[1180px] flex-col pb-16 lg:pb-0", GAP)} data-testid="contact-detail">

      {/* Fil d'Ariane */}
      <div className="flex flex-wrap items-center gap-2 text-[13px] text-[#64748b]">
        <button data-testid="contact-back-btn" onClick={() => navigate("/contacts")}
          className="inline-flex items-center gap-1.5 font-medium text-[#64748b] transition-colors hover:text-[#005bd3]">
          <ArrowLeft size={15} /> Contacts
        </button>
        <span className="text-[#d0d0d0]">/</span>
        <span className="font-semibold text-[#0f172a]">{fullName}</span>
      </div>

      {/* Carte identité */}
      <section className="overflow-hidden rounded-2xl border border-[#e3e3e3] bg-white shadow-[0_1px_2px_rgba(15,23,42,.04)]" data-testid="contact-identity-card">
        <div className="h-1.5 bg-gradient-to-r from-[#005bd3] to-[#5aa9ff]" />
        <div className="flex flex-wrap items-start gap-[clamp(12px,2vw,20px)] p-[clamp(16px,2.2vw,24px)]">
          <div className="flex h-16 w-16 flex-none items-center justify-center rounded-[18px] bg-[#e8f1fd] font-heading text-2xl font-bold tracking-[-.02em] text-[#005bd3]">
            {initials(contact.prenom, contact.nom)}
          </div>

          <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-2">
            <h1 className="m-0 font-heading text-[clamp(22px,2.6vw,30px)] font-extrabold leading-[1.15] tracking-[-.02em] text-[#0f172a]">{fullName}</h1>
            <div className="flex flex-wrap items-center gap-2 text-sm text-[#475569]">
              {contact.fonction && <span className="font-medium">{contact.fonction}</span>}
              {contact.fonction && company && <span className="text-[#d0d0d0]">·</span>}
              {company && (
                <button data-testid="contact-linked-company" onClick={() => navigate(`/entreprises/${company.id}`)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#f2f4f7] px-2.5 py-[3px] text-[13px] font-semibold text-[#344054] transition-colors hover:bg-[#e8f1fd] hover:text-[#005bd3]">
                  <Building2 size={13} /> {company.nom}
                </button>
              )}
              {company?.status && (
                <span className="inline-flex items-center rounded-full bg-[#eaf4ff] px-2.5 py-[3px] text-xs font-medium text-[#00527c]">{company.status}</span>
              )}
              {!company && (
                <span className="inline-flex items-center rounded-full bg-[#f2f4f7] px-2.5 py-[3px] text-xs font-medium text-[#64748b]">Contact libre</span>
              )}
            </div>
            <div className="flex flex-wrap gap-4 text-[13px] text-[#64748b]">
              {contact.telephone && (
                <span className="inline-flex items-center gap-1.5"><Phone size={14} className="text-[#94a3b8]" /><span className="font-mono">{contact.telephone}</span></span>
              )}
              {contact.email && (
                <span className="inline-flex items-center gap-1.5 break-all"><Mail size={14} className="shrink-0 text-[#94a3b8]" />{contact.email}</span>
              )}
              {contact.source === "appel_entrant" && (
                <span className="inline-flex items-center gap-1.5 text-xs text-[#94a3b8]"><PhoneIncoming size={13} /> Créé automatiquement suite à un appel entrant</span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="contact-detail-call-btn" onClick={callContact} disabled={!contact.telephone || !tw}
              title={!contact.telephone ? "Aucun numéro" : !tw ? "Téléphonie indisponible" : undefined}
              className={cn(BTN_CALL, "h-11 px-[18px]")}>
              <PhoneCall size={16} /> Appeler
            </button>
            <button data-testid="contact-email-btn" onClick={emailContact} disabled={!contact.email}
              title={!contact.email ? "Aucune adresse email" : undefined} className={cn(BTN, "h-11 px-4")}>
              <Mail size={16} className="text-[#616161]" /> Email
            </button>
            <button data-testid="contact-rdv-btn" onClick={() => setModal("rdv")} disabled={!company}
              title={!company ? "Liez une entreprise pour planifier un rendez-vous" : undefined} className={cn(BTN, "h-11 px-4")}>
              <CalendarPlus size={16} className="text-[#616161]" /> RDV
            </button>
            <div className="relative" ref={menuRef}>
              <button data-testid="contact-more-btn" onClick={() => setMenu((m) => !m)} aria-label="Plus d'actions"
                aria-expanded={menu} className={cn(BTN, "h-11 w-11 text-[#64748b] hover:text-[#0f172a]")}>
                <MoreHorizontal size={18} />
              </button>
              {menu && (
                <div className="absolute right-0 top-12 z-20 w-56 overflow-hidden rounded-xl border border-[#e3e3e3] bg-white p-1 shadow-[0_20px_44px_rgba(15,23,42,.14)]" data-testid="contact-more-menu">
                  {company && (
                    <button onClick={() => { setMenu(false); navigate(`/entreprises/${company.id}`); }}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[13px] text-[#334155] transition-colors hover:bg-[#f2f4f7]">
                      <ExternalLink size={14} className="text-[#64748b]" /> Ouvrir l'entreprise
                    </button>
                  )}
                  <button data-testid="contact-delete-btn" onClick={del}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[13px] text-[#d72c0d] transition-colors hover:bg-[#fdeceb]">
                    <Trash2 size={14} /> Supprimer le contact
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Indicateurs */}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] border-t border-[#ebebeb]" data-testid="contact-stats">
          {[
            { key: "last", icon: Clock, bg: "#f2f4f7", fg: "#616161", value: lastLabel, label: "Dernier échange" },
            { key: "total", icon: PhoneCall, bg: "#e7f5f1", fg: "#008060", value: String(stats.total), label: "Échanges au total" },
            { key: "rate", icon: TrendingUp, bg: "#eaf4ff", fg: "#00527c", value: stats.rate, label: "Taux de réponse" },
            { key: "next", icon: CalendarClock, bg: "#fdf3e7", fg: "#8a6116", value: nextTask ? fmtShort(nextTask.due_date) : "—", label: "Prochaine relance" },
          ].map((s) => (
            <div key={s.key} data-testid={`contact-stat-${s.key}`}
              className="flex items-center gap-3 border-l border-[#ebebeb] px-[clamp(16px,2.2vw,24px)] py-3.5 first:border-l-0">
              <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[10px]" style={{ background: s.bg, color: s.fg }}>
                <s.icon size={17} />
              </span>
              <span className="min-w-0">
                <span className="block font-heading text-[17px] font-bold tracking-[-.01em] text-[#0f172a]">{history ? s.value : "…"}</span>
                <span className="block truncate text-xs text-[#64748b]">{s.label}</span>
              </span>
            </div>
          ))}
        </div>
      </section>

      <div className={cn("grid grid-cols-[repeat(auto-fit,minmax(min(340px,100%),1fr))] items-start", GAP)}>

        {/* Colonne gauche */}
        <div className={cn("flex flex-col", GAP)}>
          <section className={CARD} data-testid="contact-fields-card">
            <div className="mb-3.5 flex items-center justify-between gap-3">
              <h2 className={H2}><IdCard size={17} className="text-[#616161]" /> Coordonnées</h2>
              <button data-testid={editing ? "contact-save-btn" : "contact-edit-btn"} onClick={editing ? saveForm : () => setEditing(true)} disabled={saving}
                className="inline-flex h-[34px] items-center gap-1.5 rounded-[9px] border border-[#e3e3e3] bg-white px-3 text-[13px] font-semibold text-[#005bd3] transition-colors hover:bg-[#f9fafb] disabled:opacity-50">
                {editing ? <Check size={14} /> : <Pencil size={14} />} {editing ? (saving ? "Enregistrement…" : "Terminer") : "Modifier"}
              </button>
            </div>

            {!editing ? (
              <div className="flex flex-col">
                {readRows.map((r) => (
                  <div key={r.key} className="flex items-center gap-3 border-b border-[#ebebeb] py-[11px] last:border-b-0" data-testid={`contact-row-${r.key}`}>
                    <span className="w-[104px] flex-none text-xs font-semibold uppercase tracking-[.05em] text-[#94a3b8]">{r.label}</span>
                    <span className={cn("min-w-0 flex-1 truncate text-sm font-medium", r.value ? "text-[#0f172a]" : "text-[#cbd5e1]", r.mono && "font-mono")}>
                      {r.value || "—"}
                    </span>
                    {r.value && (r.action === "phone" ? (
                      <button data-testid={`contact-copy-${r.key}`} onClick={callContact} disabled={!tw} title="Appeler"
                        className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-[#cbd5e1] transition-colors hover:bg-[#e7f5f1] hover:text-[#008060] disabled:opacity-40">
                        <Phone size={15} />
                      </button>
                    ) : r.action === "mail" ? (
                      <button data-testid={`contact-copy-${r.key}`} onClick={emailContact} title="Envoyer un email"
                        className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-[#cbd5e1] transition-colors hover:bg-[#eaf4ff] hover:text-[#005bd3]">
                        <Mail size={15} />
                      </button>
                    ) : (
                      <button data-testid={`contact-copy-${r.key}`} onClick={() => copy(r.value, r.label)} title="Copier"
                        className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-[#cbd5e1] transition-colors hover:bg-[#f2f4f7] hover:text-[#0f172a]">
                        <Copy size={15} />
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3.5">
                {FIELDS.map(([k, label]) => (
                  <label key={k} className="block">
                    <span className={LABEL}>{label}</span>
                    <input data-testid={`contact-field-${k}`} value={form[k]}
                      onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                      className={cn(INPUT, k === "telephone" && "font-mono")} />
                  </label>
                ))}
                <div className="col-span-full">
                  <span className={LABEL}>Adresse</span>
                  {/* Suggestions officielles : le code postal et la ville se
                      remplissent avec l'adresse retenue. */}
                  <AddressAutocomplete
                    testId="contact-field-adresse" value={form.adresse}
                    onChange={(v) => setForm((f) => ({ ...f, adresse: v }))}
                    onSelect={(a) => setForm((f) => ({
                      ...f, adresse: a.adresse,
                      code_postal: a.code_postal || f.code_postal,
                      ville: a.ville || f.ville,
                    }))}
                    inputClassName={INPUT} />
                </div>
                <label className="block">
                  <span className={LABEL}>Code postal</span>
                  <input data-testid="contact-field-code_postal" value={form.code_postal}
                    onChange={(e) => setForm((f) => ({ ...f, code_postal: e.target.value }))} className={cn(INPUT, "font-mono")} />
                </label>
                <label className="block">
                  <span className={LABEL}>Ville</span>
                  <input data-testid="contact-field-ville" value={form.ville}
                    onChange={(e) => setForm((f) => ({ ...f, ville: e.target.value }))} className={INPUT} />
                </label>
                <div className="col-span-full flex justify-end gap-2 pt-1">
                  <button data-testid="contact-cancel-btn" onClick={cancelEdit} disabled={saving} className={cn(BTN_SMALL, "h-10 px-4")}>Annuler</button>
                  <button data-testid="contact-save-form-btn" onClick={saveForm} disabled={saving} className={cn(BTN_BLUE, "h-10 px-4")}>
                    <Check size={15} /> {saving ? "Enregistrement…" : "Enregistrer"}
                  </button>
                </div>
              </div>
            )}
          </section>

          <section className={CARD} data-testid="contact-notes-card">
            <h2 className={cn(H2, "mb-3")}><StickyNote size={17} className="text-[#616161]" /> Notes</h2>
            {noteEditing ? (
              <>
                <textarea data-testid="contact-field-notes" rows={4} value={noteDraft} autoFocus
                  onChange={(e) => setNoteDraft(e.target.value)} placeholder="Ce qu'il faut retenir de ce contact…"
                  className="w-full resize-y rounded-xl border border-[#d0d0d0] bg-white px-3.5 py-3 text-sm leading-relaxed text-[#303030] outline-none transition-all focus:border-[#005bd3] focus:ring-[3px] focus:ring-[rgba(0,91,211,.16)]" />
                <div className="mt-3 flex justify-end gap-2">
                  <button onClick={() => setNoteEditing(false)} disabled={saving} className={cn(BTN_SMALL, "h-9 px-3.5")}>Annuler</button>
                  <button data-testid="contact-note-save" onClick={saveNote} disabled={saving} className={cn(BTN_BLUE, "h-9 px-3.5")}>
                    <Check size={14} /> Enregistrer
                  </button>
                </div>
              </>
            ) : (
              <>
                {contact.notes ? (
                  <div className="whitespace-pre-line rounded-xl border border-[#e3e3e3] bg-[#fafafa] px-4 py-3.5 text-sm leading-relaxed text-[#303030] [text-wrap:pretty]" data-testid="contact-notes">
                    {contact.notes}
                  </div>
                ) : (
                  <p className="m-0 text-sm text-[#94a3b8]">Aucune note pour l'instant.</p>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button data-testid="contact-note-edit" onClick={startNote}
                    className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-dashed border-[#d0d0d0] bg-white px-3 text-[13px] font-semibold text-[#64748b] transition-colors hover:border-[#616161] hover:text-[#005bd3]">
                    {contact.notes ? <><Pencil size={14} /> Modifier la note</> : <><Plus size={15} /> Ajouter une note</>}
                  </button>
                  {contact.notes && contact.updated_at && (
                    <span className="text-xs text-[#94a3b8]">Modifiée {relTime(contact.updated_at)?.toLowerCase()}</span>
                  )}
                </div>
              </>
            )}
          </section>

          <section className={CARD} data-testid="contact-company-card">
            <h2 className={cn(H2, "mb-3")}><Building2 size={17} className="text-[#616161]" /> Entreprise liée</h2>
            {company ? (
              <div className="flex flex-wrap items-center gap-3.5">
                <span className="flex h-[46px] w-[46px] flex-none items-center justify-center rounded-xl bg-[#f2f4f7] font-heading text-base font-bold text-[#344054]">
                  {initials(...(company.nom || "").split(/\s+/))}
                </span>
                <span className="min-w-0 flex-[1_1_160px]">
                  <span className="block truncate text-[15px] font-semibold text-[#0f172a]">{company.nom}</span>
                  <span className="block truncate text-[13px] text-[#64748b]">
                    {[
                      [companyInfo?.ville || company.ville, companyInfo?.code_postal ? `(${String(companyInfo.code_postal).slice(0, 2)})` : null].filter(Boolean).join(" ") || null,
                      companyInfo?.naf ? `NAF ${companyInfo.naf}` : null,
                      companyInfo?.tranche_effectif || (companyInfo?.effectif ? `${companyInfo.effectif} salariés` : null),
                    ].filter(Boolean).join(" · ") || company.status || "—"}
                  </span>
                </span>
                <span className="flex gap-2">
                  <button data-testid="contact-open-company-btn" onClick={() => navigate(`/entreprises/${company.id}`)}
                    className={cn(BTN, "h-[38px] rounded-[10px] px-3.5 text-[13px]")}>
                    <ExternalLink size={14} /> Ouvrir
                  </button>
                  <button data-testid="contact-unlink-btn" onClick={unlink} aria-label="Délier" title="Délier de l'entreprise"
                    className="inline-flex h-[38px] w-[38px] items-center justify-center rounded-[10px] border border-[#e3e3e3] bg-white text-[#94a3b8] transition-colors hover:border-[#fecdd3] hover:text-[#f43f5e]">
                    <Unlink size={15} />
                  </button>
                </span>
              </div>
            ) : (
              <div className="relative">
                <p className="mb-2 text-xs text-[#94a3b8]">Contact libre — recherche une entreprise pour le lier, ou garde-le tel quel.</p>
                <input data-testid="contact-company-search" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher une entreprise…" className={INPUT} />
                {search.trim().length >= 2 && (
                  <button data-testid="contact-create-company" onClick={creerEtLier} disabled={creating}
                    className="mt-2 flex items-center gap-1.5 text-[12.5px] font-medium text-[#005bd3] transition-colors hover:text-[#00479f] disabled:opacity-50">
                    <Plus size={14} /> {creating ? "Création…" : `Créer l'entreprise « ${search.trim()} » et la lier`}
                  </button>
                )}
                {results.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-[#e3e3e3] bg-white shadow-[0_20px_44px_rgba(15,23,42,.14)]" data-testid="contact-company-results">
                    {results.map((c) => (
                      <button key={c.id} data-testid={`link-company-${c.id}`} onClick={() => linkCompany(c.id, c.nom)}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-[#e8f1fd]">
                        <span className="font-medium text-[#334155]">{c.nom}</span>
                        <span className="text-xs text-[#94a3b8]">{c.ville || ""}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        </div>

        {/* Colonne droite */}
        <div className={cn("flex flex-col", GAP)}>
          <section className={cn(CARD, "border-l-[3px] border-l-[#005bd3]")} data-testid="contact-next-action">
            <div className="flex items-center justify-between gap-3">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[.06em] text-[#005bd3]">
                <Target size={15} /> Prochaine action
              </span>
              {due && (
                <span className={cn("rounded-full px-[9px] py-[3px] text-xs font-semibold",
                  due.late ? "bg-[#fdeceb] text-[#b42318]" : "bg-[#f2f4f7] text-[#303030]")}>{due.label}</span>
              )}
            </div>
            {nextTask ? (
              <>
                <p className="mb-0 mt-2.5 font-heading text-[17px] font-bold leading-[1.35] text-[#0b1524] [text-wrap:pretty]" data-testid="contact-next-action-title">{nextTask.title}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button data-testid="contact-task-done" onClick={finishTask} className={cn(BTN_BLUE, "h-10 px-[15px]")}>
                    <Check size={15} /> Marquer faite
                  </button>
                  <button data-testid="contact-task-postpone" onClick={postponeTask} className={cn(BTN_SMALL, "h-10 px-[15px]")}>
                    <Clock size={15} /> Reporter
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="mb-0 mt-2.5 font-heading text-[17px] font-bold leading-[1.35] text-[#0b1524]">Aucune action planifiée</p>
                <p className="mb-0 mt-1 text-[13px] text-[#64748b]">
                  {company ? "Planifie une relance pour ne pas laisser ce contact retomber." : "Lie ce contact à une entreprise pour planifier une action."}
                </p>
                <div className="mt-3">
                  <button data-testid="contact-task-plan" onClick={() => setModal("task")} disabled={!company} className={cn(BTN_BLUE, "h-10 px-[15px]")}>
                    <Plus size={15} /> Planifier
                  </button>
                </div>
              </>
            )}
          </section>

          <section className={CARD} data-testid="contact-history-card">
            <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3">
              <h2 className={H2}><History size={17} className="text-[#616161]" /> Historique</h2>
              <div className="flex gap-1 rounded-[10px] bg-[#f2f4f7] p-[3px]">
                {HISTORY_TABS.map(([k, label]) => (
                  <button key={k} data-testid={`contact-history-tab-${k}`} onClick={() => { setHistoryTab(k); setShowAll(false); }}
                    className={cn("h-[30px] rounded-lg px-3 text-[13px] font-semibold transition-colors",
                      historyTab === k ? "bg-white text-[#0f172a] shadow-[0_1px_2px_rgba(15,23,42,.12)]" : "text-[#64748b] hover:text-[#0f172a]")}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {!history ? <Loader /> : shown.length === 0 ? (
              <p className="py-6 text-center text-sm text-[#94a3b8]">
                {historyTab === "calls" ? "Aucun appel avec ce contact" : historyTab === "activities" ? "Aucune activité liée à ce contact" : "Aucun échange pour l'instant"}
              </p>
            ) : (
              <div className="flex flex-col" data-testid="contact-history-timeline">
                {visibleHistory.map((e) => (
                  <div key={e.id} className="group flex gap-3.5" data-testid={`contact-history-${e.kind}`}>
                    <div className="flex w-[34px] flex-none flex-col items-center">
                      <span className="flex h-[34px] w-[34px] items-center justify-center rounded-[10px]" style={{ background: e.bg, color: e.fg }}>
                        <e.icon size={16} />
                      </span>
                      <span className="my-1.5 w-0.5 flex-1 rounded-sm bg-[#ebebeb] group-last:hidden" />
                    </div>
                    <div className="min-w-0 flex-1 pb-[18px] group-last:pb-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-[#0f172a]">{e.title}</span>
                        {e.tag && (
                          <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: e.tagBg, color: e.tagFg }}>{e.tag}</span>
                        )}
                      </div>
                      {e.detail && <p className="mb-0 mt-1 text-[13px] leading-[1.55] text-[#475569] [text-wrap:pretty]">{e.detail}</p>}
                      <div className="mt-1.5 text-xs text-[#94a3b8]">{e.meta}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {history && shown.length > HISTORY_PREVIEW && (
              <button data-testid="contact-history-more" onClick={() => setShowAll((v) => !v)}
                className="mt-2 h-10 w-full rounded-[11px] border border-[#e3e3e3] bg-white text-[13px] font-semibold text-[#005bd3] transition-colors hover:bg-[#f9fafb]">
                {showAll ? "Réduire l'historique" : `Voir tout l'historique (${shown.length})`}
              </button>
            )}
          </section>
        </div>
      </div>

      {/* Barre d'action mobile : posée sur la barre du softphone (56 px,
          collée en bas) quand elle est là, et au-dessus de la barre d'onglets
          du shell (--bottom-nav-h). Sur grand écran, les actions de l'en-tête
          suffisent. */}
      <div className="fixed left-0 right-0 z-30 flex items-center gap-2.5 border-t border-[#e3e3e3] bg-white/[.92] px-[clamp(12px,2.5vw,28px)] py-2.5 backdrop-blur-[8px] lg:hidden"
        style={{ bottom: `calc(${softphoneVisible ? 56 : 0}px + var(--bottom-nav-h, 0px))` }} data-testid="contact-mobile-bar">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <span className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[10px] bg-[#e8f1fd] text-[13px] font-bold text-[#005bd3]">
            {initials(contact.prenom, contact.nom)}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-[13px] font-semibold text-[#0f172a]">{fullName}</span>
            <span className="block text-[11px] text-[#64748b]">{stats.last ? `Dernier échange ${lastLabel.toLowerCase()}` : "Aucun échange"}</span>
          </span>
        </div>
        <button data-testid="contact-mobile-email" onClick={emailContact} disabled={!contact.email} aria-label="Envoyer un email"
          className={cn(BTN, "h-[46px] w-[46px] rounded-[13px] text-[#005bd3]")}>
          <Mail size={18} />
        </button>
        <button data-testid="contact-mobile-call" onClick={callContact} disabled={!contact.telephone || !tw}
          className={cn(BTN_CALL, "h-[46px] rounded-[13px] px-5 text-[15px] font-bold")}>
          <PhoneCall size={18} /> Appeler
        </button>
      </div>

      {modal === "email" && company && (
        <ContactEmailModal contact={contact} onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadHistory(); }} />
      )}
      {(modal === "rdv" || modal === "task") && company && (
        <ContactTaskModal contact={contact} fullName={fullName} defaultType={modal === "rdv" ? "MEETING" : "FOLLOW_UP"}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); loadTasks(); }} />
      )}
    </div>
  );
}
